import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { getCompetitionLeaderboard } from "../../../../../lib/leaderboard";
import { supabase } from "../../../../../lib/supabase";
import {
  adminOverrideWinnerSelection,
  approveWinnerSelection,
  formatUsd,
  getCompetitionWinnerConfig,
  getLatestPendingSelection,
  placeLabel,
  regenerateWinnerSelection,
  rejectWinnerSelection,
  type PrizeStructureRow,
  type SelectionDetail,
  type TeamSplitPolicy,
} from "../../../../../lib/winners";
import type { LeaderboardRow } from "../../../../../types/database";
import { RADII, SPACING, STROKES } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminModal,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../../../components/admin/ui";
import { SectionHeader } from "../../../../../components/aspects/SectionHeader";
import { StatusPill } from "../../../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;
const PAGE_MAX_W = 1100;

type CompetitionInfo = {
  id: string;
  name: string;
  status: string;
  company_name: string | null;
  team_split_policy: TeamSplitPolicy;
};

export default function AdminWinnersReviewScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const competitionId = typeof id === "string" ? id : "";

  const [comp, setComp] = useState<CompetitionInfo | null>(null);
  const [config, setConfig] = useState<{ winner_count: number; prize_structure: PrizeStructureRow[] } | null>(null);
  const [selection, setSelection] = useState<SelectionDetail | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const load = useCallback(async () => {
    if (!competitionId) return;
    setLoading(true);
    try {
      const { data: compRow } = await supabase
        .from("competitions")
        .select("id, name, status, team_split_policy, companies(name)")
        .eq("id", competitionId)
        .maybeSingle();
      if (compRow) {
        type Row = {
          id: string;
          name: string;
          status: string;
          team_split_policy: TeamSplitPolicy;
          companies: { name: string } | { name: string }[] | null;
        };
        const row = compRow as Row;
        const c = Array.isArray(row.companies) ? row.companies[0] : row.companies;
        setComp({
          id: row.id,
          name: row.name,
          status: row.status,
          team_split_policy: row.team_split_policy,
          company_name: c?.name ?? null,
        });
      }
      const cfg = await getCompetitionWinnerConfig(competitionId);
      setConfig(cfg ? { winner_count: cfg.winner_count, prize_structure: cfg.prize_structure } : null);
      setSelection(await getLatestPendingSelection(competitionId));
      try {
        setLeaderboard(await getCompetitionLeaderboard(competitionId));
      } catch {
        setLeaderboard([]);
      }
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  async function onApprove() {
    if (!selection) return;
    setBusy(true);
    setErrMsg(null);
    const { count, error } = await approveWinnerSelection(selection.selection.id);
    setBusy(false);
    if (error) {
      setErrMsg(error.message);
      return;
    }
    setStatusMsg("Approved — created " + count + " disbursement record" + (count === 1 ? "" : "s") + ".");
    setTimeout(() => setStatusMsg(null), 3000);
    void load();
  }

  async function onRegenerate() {
    setBusy(true);
    setErrMsg(null);
    const { error } = await regenerateWinnerSelection(competitionId);
    setBusy(false);
    if (error) {
      setErrMsg(error.message);
      return;
    }
    setStatusMsg("Regenerated from leaderboard.");
    setTimeout(() => setStatusMsg(null), 2400);
    void load();
  }

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <AdminLoading theme={THEME} label="Loading winners…" />
      </View>
    );
  }

  if (!comp) {
    return (
      <View style={{ flex: 1, padding: SPACING.xl, backgroundColor: THEME.pageBg }}>
        <AdminError theme={THEME} message="Competition not found." onRetry={() => void load()} />
      </View>
    );
  }

  const sel = selection?.selection ?? null;
  const teams = selection?.teams ?? [];
  const isPending = sel?.status === "pending_admin_review";
  const isApproved = sel?.status === "approved";
  const isRejected = sel?.status === "rejected";
  const showEmpty = !selection;

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.lg, maxWidth: PAGE_MAX_W }}>
        <Pressable
          onPress={() => router.replace("/admin/competitions" as Href)}
          style={[Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null, { paddingVertical: 4, marginBottom: 6 }]}
        >
          <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
            ← All competitions
          </Text>
        </Pressable>

        <AdminPageHeader
          theme={THEME}
          eyebrow="Review winners"
          title={comp.name}
          subtitle={`${comp.company_name ?? "—"} · status ${comp.status}`}
        />

        {errMsg ? (
          <View style={{ marginBottom: SPACING.md }}>
            <AdminError theme={THEME} message={errMsg} onRetry={() => void load()} />
          </View>
        ) : null}
        {statusMsg ? (
          <View style={{ marginBottom: SPACING.md }}>
            <AdminBanner theme={THEME} tone="success" message={statusMsg} />
          </View>
        ) : null}

        {showEmpty ? (
          <View
            style={{
              padding: 22,
              borderRadius: RADII.md,
              backgroundColor: THEME.surfaceBg,
              borderWidth: STROKES.hairline,
              borderColor: THEME.border,
            }}
          >
            <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 22, color: THEME.textOnSurface }}>
              No winner selection yet
            </Text>
            <Text style={{ marginTop: 6, fontFamily: THEME.bodyFont, fontSize: 12.5, color: THEME.textOnSurfaceDim, maxWidth: 540 }}>
              The auto-trigger fires when the competition transitions to closed; if it didn't (or
              all judges hadn't submitted yet), regenerate manually here.
            </Text>
            <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
              <AdminButton theme={THEME} label="Regenerate from leaderboard" loading={busy} onPress={() => void onRegenerate()} />
              <AdminButton theme={THEME} label="Pick different winners" variant="outline" onPress={() => setOverrideOpen(true)} />
            </View>
          </View>
        ) : null}

        {sel ? (
          <View>
            <View
              style={{
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderRadius: RADII.sm,
                backgroundColor: THEME.surfaceBg,
                borderWidth: STROKES.hairline,
                borderColor: THEME.border,
              }}
            >
              <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: THEME.accent }}>
                {sel.source === "leaderboard_auto" ? "Auto-generated from leaderboard" : "Manual override"}
                {isApproved ? " · approved" : isRejected ? " · rejected" : " · pending review"}
              </Text>
              <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
                Generated{" "}
                {new Date(sel.generated_at).toLocaleString("en-US", {
                  timeZone: "America/New_York",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  timeZoneName: "short",
                })}
              </Text>
              {sel.notes ? (
                <Text style={{ marginTop: 6, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim, fontStyle: "italic" }}>
                  Override note: {sel.notes}
                </Text>
              ) : null}
              {sel.review_notes ? (
                <Text style={{ marginTop: 6, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim, fontStyle: "italic" }}>
                  Review note: {sel.review_notes}
                </Text>
              ) : null}
            </View>

            <View style={{ marginTop: SPACING.lg }}>
              <SectionHeader theme={THEME} title="Selection" tone="dark" />
              <View style={{ gap: 10 }}>
                {teams.map((t) => (
                  <View
                    key={t.team_id}
                    style={{
                      padding: 16,
                      borderRadius: RADII.md,
                      backgroundColor: THEME.surfaceRaisedBg,
                      borderWidth: STROKES.hairline,
                      borderColor: THEME.border,
                    }}
                  >
                    <View style={{ flexDirection: "row", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                      <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.accent }}>
                        {placeLabel(t.place)}
                      </Text>
                      <Text style={{ flex: 1, fontFamily: THEME.displayMediumFont, fontSize: 22, color: THEME.textOnSurface }}>
                        {t.team_name}
                      </Text>
                      <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 22, color: THEME.accent }}>
                        {formatUsd(t.amount_cents)}
                      </Text>
                    </View>
                    <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
                      Coravu score {t.weighted_score != null ? t.weighted_score.toFixed(2) : "—"} ·{" "}
                      {t.members.length} member{t.members.length === 1 ? "" : "s"}
                    </Text>
                    <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                      {t.members.map((m) => {
                        const missing = !m.full_name && !m.email;
                        return (
                          <View
                            key={m.student_id}
                            style={{
                              paddingHorizontal: 9,
                              paddingVertical: 4,
                              borderRadius: RADII.full,
                              backgroundColor: "transparent",
                              borderWidth: STROKES.hairline,
                              borderColor: missing ? THEME.destructive : THEME.border,
                            }}
                          >
                            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 10.5, color: missing ? THEME.destructive : THEME.textOnSurfaceDim }}>
                              {m.full_name ?? m.email ?? "(missing profile)"}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                ))}
              </View>
            </View>

            {/* Disbursement preview */}
            {isPending && comp ? (
              <DisbursementPreview teams={teams} splitPolicy={comp.team_split_policy} />
            ) : null}

            {/* Actions */}
            {isPending ? (
              <View style={{ marginTop: SPACING.lg, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                <AdminButton theme={THEME} label="Approve & create disbursements" loading={busy} onPress={() => void onApprove()} />
                <AdminButton theme={THEME} label="Pick different winners" variant="outline" onPress={() => setOverrideOpen(true)} />
                <AdminButton theme={THEME} label="Reject" variant="destructive" onPress={() => setRejectOpen(true)} />
              </View>
            ) : null}

            {(isApproved || isRejected) && (
              <View style={{ marginTop: SPACING.md, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                <AdminButton theme={THEME} label="See disbursements →" variant="outline" size="sm" onPress={() => router.push("/admin/disbursements" as Href)} />
                {isRejected ? (
                  <AdminButton theme={THEME} label="Regenerate from leaderboard" size="sm" loading={busy} onPress={() => void onRegenerate()} />
                ) : null}
              </View>
            )}
          </View>
        ) : null}

        {/* Full leaderboard for context */}
        <View style={{ marginTop: SPACING.xl }}>
          <SectionHeader theme={THEME} title="Full leaderboard for context" tone="dark" />
          {leaderboard.length === 0 ? (
            <View style={{ padding: 14, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
              <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceFaint }}>
                No leaderboard rows yet.
              </Text>
            </View>
          ) : (
            <ListRowGroup theme={THEME}>
              {leaderboard.map((lb, i) => (
                <ListRow
                  key={lb.team_id}
                  theme={THEME}
                  first={i === 0}
                  leading={
                    <Text style={{ width: 34, fontFamily: THEME.displayMediumFont, fontSize: 16, color: lb.placement != null ? THEME.accent : THEME.textOnSurfaceFaint }}>
                      {lb.placement != null ? "#" + lb.placement : "—"}
                    </Text>
                  }
                  title={lb.team_name}
                  trailing={
                    <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 16, color: THEME.accent }}>
                      {lb.final_score != null ? lb.final_score.toFixed(2) : "—"}
                    </Text>
                  }
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>

      {overrideOpen && config && comp ? (
        <OverrideModal
          competitionId={competitionId}
          winnerCount={config.winner_count}
          prizeStructure={config.prize_structure}
          leaderboard={leaderboard}
          onClose={() => setOverrideOpen(false)}
          onSaved={() => {
            setOverrideOpen(false);
            void load();
          }}
        />
      ) : null}

      {rejectOpen && sel ? (
        <RejectModal
          selectionId={sel.id}
          onClose={() => setRejectOpen(false)}
          onRejected={() => {
            setRejectOpen(false);
            void load();
          }}
        />
      ) : null}
    </ScrollView>
  );
}

function DisbursementPreview({
  teams,
  splitPolicy,
}: {
  teams: SelectionDetail["teams"];
  splitPolicy: TeamSplitPolicy;
}) {
  const lines = useMemo(() => {
    const out: Array<{ key: string; label: string; amount_cents: number; taxRequired: boolean }> = [];
    for (const t of teams) {
      if (t.members.length === 0) continue;
      if (splitPolicy === "captain_takes_all") {
        out.push({
          key: t.team_id + ":captain",
          label: "Captain of " + t.team_name + " — gets full team prize",
          amount_cents: t.amount_cents,
          taxRequired: t.amount_cents >= 60000,
        });
      } else {
        const per = Math.floor(t.amount_cents / t.members.length);
        for (const m of t.members) {
          out.push({
            key: t.team_id + ":" + m.student_id,
            label: (m.full_name ?? m.email ?? "(member)") + " — from " + t.team_name,
            amount_cents: per,
            taxRequired: per >= 60000,
          });
        }
      }
    }
    return out;
  }, [teams, splitPolicy]);

  if (lines.length === 0) return null;
  const totalCents = lines.reduce((s, l) => s + l.amount_cents, 0);

  return (
    <View
      style={{
        marginTop: SPACING.lg,
        padding: 16,
        borderRadius: RADII.md,
        backgroundColor: THEME.surfaceBg,
        borderWidth: STROKES.hairline,
        borderColor: THEME.border,
      }}
    >
      <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: THEME.accent }}>
        Disbursement preview ({lines.length})
      </Text>
      <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceFaint }}>
        On approve, these {lines.length} disbursement record{lines.length === 1 ? "" : "s"} will be
        created. Each {">"} $600 will be flagged for W-9 collection.
      </Text>
      <View style={{ marginTop: 10, gap: 6 }}>
        {lines.map((l) => (
          <View key={l.key} style={{ flexDirection: "row", alignItems: "center", gap: 12, paddingVertical: 4 }}>
            <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
              {l.label}
            </Text>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.textOnSurface }}>
              {formatUsd(l.amount_cents)}
            </Text>
            <StatusPill theme={THEME} label={l.taxRequired ? "W-9 required" : "No 1099"} tone={l.taxRequired ? "amber" : "grey"} showDot={false} />
          </View>
        ))}
      </View>
      <Text style={{ marginTop: 10, fontFamily: THEME.bodyMediumFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
        Total: {formatUsd(totalCents)}
      </Text>
    </View>
  );
}

function OverrideModal({
  competitionId,
  winnerCount,
  prizeStructure,
  leaderboard,
  onClose,
  onSaved,
}: {
  competitionId: string;
  winnerCount: number;
  prizeStructure: PrizeStructureRow[];
  leaderboard: LeaderboardRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const ranked = useMemo(
    () =>
      leaderboard
        .filter((lb) => lb.placement != null)
        .sort((a, b) => (a.placement ?? 999) - (b.placement ?? 999)),
    [leaderboard]
  );

  const [picks, setPicks] = useState<Record<number, string>>(() => {
    const out: Record<number, string> = {};
    for (let p = 1; p <= winnerCount; p++) {
      const candidate = ranked[p - 1];
      if (candidate) out[p] = candidate.team_id;
    }
    return out;
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function setPick(place: number, teamId: string) {
    setPicks((prev) => ({ ...prev, [place]: teamId }));
  }

  async function submit() {
    setErr(null);
    if (!notes.trim()) {
      setErr("Notes are required when overriding the auto-selection.");
      return;
    }
    const selected = Object.values(picks);
    const dupes = selected.length !== new Set(selected).size;
    if (selected.length !== winnerCount || selected.some((s) => !s)) {
      setErr("Pick a team for every place.");
      return;
    }
    if (dupes) {
      setErr("A team can only win one place — pick distinct teams.");
      return;
    }
    setSubmitting(true);
    const winners = Object.entries(picks).map(([p, team_id]) => ({ team_id, place: Number(p) }));
    const { error } = await adminOverrideWinnerSelection({ competitionId, winners, notes: notes.trim() });
    setSubmitting(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved();
  }

  return (
    <AdminModal
      theme={THEME}
      visible
      onClose={onClose}
      maxWidth={640}
      title="Pick different winners"
      subtitle="Use this when the leaderboard didn't get it right — e.g. a team was disqualified after judging, or scoring revealed a tie that needs admin discretion. Notes are required and go into the audit trail."
      footer={
        <>
          <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={onClose} />
          <AdminButton theme={THEME} label="Replace pending selection" loading={submitting} onPress={() => void submit()} />
        </>
      }
    >
      <View style={{ gap: 12 }}>
        {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}

        <View style={{ gap: 10 }}>
          {Array.from({ length: winnerCount }, (_, i) => i + 1).map((place) => {
            const prize = prizeStructure.find((p) => p.place === place);
            return (
              <View
                key={place}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  borderRadius: RADII.sm,
                  borderWidth: STROKES.hairline,
                  borderColor: THEME.border,
                  backgroundColor: THEME.altBg,
                }}
              >
                <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.accent }}>
                  {placeLabel(place, prize?.label)} {prize ? "· " + formatUsd(prize.amount_cents) : ""}
                </Text>
                <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                  {ranked.slice(0, 12).map((lb) => (
                    <FilterChip
                      key={lb.team_id}
                      theme={THEME}
                      label={`#${lb.placement ?? "—"} ${lb.team_name} ${lb.final_score != null ? `(${lb.final_score.toFixed(2)})` : ""}`}
                      active={picks[place] === lb.team_id}
                      onPress={() => setPick(place, lb.team_id)}
                    />
                  ))}
                </View>
              </View>
            );
          })}
        </View>

        <AdminInput
          theme={THEME}
          label="Why are you overriding the auto-selection? *"
          value={notes}
          onChangeText={setNotes}
          multiline
          minHeight={90}
          placeholder="e.g. Team Beta was disqualified post-judging for a code-of-conduct issue; replacing with Team Gamma at #2."
        />
      </View>
    </AdminModal>
  );
}

function RejectModal({
  selectionId,
  onClose,
  onRejected,
}: {
  selectionId: string;
  onClose: () => void;
  onRejected: () => void;
}) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    if (!notes.trim()) {
      setErr("Reason is required.");
      return;
    }
    setBusy(true);
    const { error } = await rejectWinnerSelection(selectionId, notes.trim());
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onRejected();
  }

  return (
    <AdminModal
      theme={THEME}
      visible
      onClose={onClose}
      title="Reject this selection"
      subtitle={'The competition will land in a "no pending selection" state — regenerate or override from the same surface afterward.'}
      footer={
        <>
          <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={onClose} />
          <AdminButton theme={THEME} label="Reject selection" variant="destructive" loading={busy} onPress={() => void submit()} />
        </>
      }
    >
      <View style={{ gap: 12 }}>
        {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}
        <AdminInput
          theme={THEME}
          label="Reason *"
          value={notes}
          onChangeText={setNotes}
          multiline
          minHeight={90}
          placeholder="e.g. Need to wait for additional judge scores before locking winners."
        />
      </View>
    </AdminModal>
  );
}
